import React from 'react';
import { Container, Row, Col, Card, Alert, Table } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import PublicSiteNavbar from '../../components/PublicSiteNavbar';
import PublicSiteFooter from '../../components/PublicSiteFooter';
import NonIndexableHelmet from '../../components/NonIndexableHelmet';
import { getLegalMeta } from '../../data/nonIndexableMeta';
import { useLocale } from '../../contexts/LocaleContext';
import { getDateLocale } from '../../utils/localePaths';
import { COMPANY, MEDIATOR } from '../../data/legalCompanyInfo';

const CgvPage = () => {
  const { locale } = useLocale();
  const dateLoc = getDateLocale(locale);
  return (
    <div className="min-vh-100 bg-light legal-page">
      <NonIndexableHelmet {...getLegalMeta('/legal/cgv')} />
      <PublicSiteNavbar />

      {/* Content */}
      <Container className="py-5">
        <Row className="justify-content-center">
          <Col lg={10} xl={8}>
            <Card className="shadow-sm border-0">
              <Card.Body className="p-3 p-md-4 p-lg-5">
                <h1 className="fw-bold mb-4">Conditions Générales de Vente (CGV)</h1>
                <p className="text-muted mb-4">
                  Dernière mise à jour : {new Date().toLocaleDateString(dateLoc)}
                </p>

                <Alert variant="info" className="border-0">
                  <i className="bi bi-info-circle me-2"></i>
                  <strong>Acceptation des CGV</strong>
                  <p className="mb-0 mt-2 small">
                    Toute commande de crédits sur Assistance Video implique l'acceptation pleine et entière
                    des présentes Conditions Générales de Vente. Si vous n'acceptez pas ces conditions,
                    veuillez ne pas effectuer de commande.
                  </p>
                </Alert>

                <hr className="my-4" />

                <section className="mb-5">
                  <h2 className="h4 fw-bold mb-3">1. Objet</h2>
                  <p>
                    Les présentes Conditions Générales de Vente (ci-après « CGV ») régissent les ventes
                    de crédits de sessions vidéo sur la plateforme <strong>Assistance Video</strong>,
                    éditée par la société LEAGORA SAS.
                  </p>
                  <p>
                    Ces CGV s'appliquent à toutes les commandes passées sur le site et prévalent sur tout
                    autre document, sauf dérogation préalable, expresse et écrite.
                  </p>
                </section>

                <section className="mb-5">
                  <h2 className="h4 fw-bold mb-3">2. Vendeur</h2>
                  <p>Les produits sont vendus par :</p>
                  <ul className="list-unstyled ms-3">
                    <li><strong>Raison sociale :</strong> {COMPANY.legalName}</li>
                    <li><strong>Capital social :</strong> {COMPANY.capital}</li>
                    <li><strong>Siège social :</strong> {COMPANY.address}</li>
                    <li><strong>SIREN :</strong> {COMPANY.siren}</li>
                    <li><strong>SIRET :</strong> {COMPANY.siret}</li>
                    <li><strong>RCS :</strong> {COMPANY.rcs}</li>
                    <li><strong>N° TVA intracommunautaire :</strong> {COMPANY.vat}</li>
                    <li><strong>Email :</strong> <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a></li>
                  </ul>
                </section>

                <section className="mb-5">
                  <h2 className="h4 fw-bold mb-3">3. Produits et services</h2>
                  
                  <h3 className="h6 fw-semibold mt-4 mb-2">3.1. Description des produits</h3>
                  <p>
                    LEAGORA propose à la vente des packs de crédits permettant d'utiliser des sessions
                    vidéo d'assistance technique. Chaque crédit correspond à une session de dépannage vidéo.
                    Le crédit est consommé automatiquement au démarrage de la session.
                    Les sessions sont illimitées en durée.
                  </p>

                  <h3 className="h6 fw-semibold mt-4 mb-2">3.2. Offres disponibles</h3>
                  <Table bordered hover responsive className="mt-3">
                    <thead className="table-light">
                      <tr>
                        <th>Pack</th>
                        <th>Nombre de sessions</th>
                        <th>Prix HT</th>
                        <th>Prix par session (HT)</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td><strong>Pack 10</strong></td>
                        <td>10 sessions</td>
                        <td>15,00 €</td>
                        <td>1,50 €</td>
                      </tr>
                      <tr>
                        <td><strong>Pack 50</strong></td>
                        <td>50 sessions</td>
                        <td>65,00 €</td>
                        <td>1,30 €</td>
                      </tr>
                      <tr>
                        <td><strong>Pack 100</strong></td>
                        <td>100 sessions</td>
                        <td>110,00 €</td>
                        <td>1,10 €</td>
                      </tr>
                    </tbody>
                  </Table>
                  <p className="small text-muted">
                    <i className="bi bi-info-circle me-1"></i>
                    Les prix sont indiqués en euros, hors taxes (HT). La TVA applicable est calculée
                    automatiquement au paiement selon le pays de facturation du client.
                  </p>

                  <h3 className="h6 fw-semibold mt-4 mb-2">3.3. Caractéristiques des crédits</h3>
                  <ul>
                    <li><strong>Durée de validité :</strong> Les crédits n'expirent jamais</li>
                    <li><strong>Durée par session :</strong> Illimitée</li>
                    <li><strong>Utilisation :</strong> 1 crédit = 1 session de dépannage vidéo</li>
                    <li><strong>Consommation :</strong> Le crédit est consommé automatiquement au démarrage de la session</li>
                    <li><strong>Transférabilité :</strong> Les crédits ne sont pas transférables entre comptes</li>
                    <li><strong>Remboursement :</strong> Les crédits ne sont pas remboursables</li>
                  </ul>
                </section>

                <section className="mb-5">
                  <h2 className="h4 fw-bold mb-3">4. Prix</h2>
                  
                  <h3 className="h6 fw-semibold mt-4 mb-2">4.1. Prix affichés</h3>
                  <p>
                    Les prix sont affichés en euros, hors taxes (HT). La TVA applicable est calculée
                    automatiquement au paiement selon le pays de facturation du client.
                  </p>

                  <h3 className="h6 fw-semibold mt-4 mb-2">4.2. Modification des prix</h3>
                  <p>
                    LEAGORA se réserve le droit de modifier ses prix à tout moment. Les prix applicables
                    sont ceux en vigueur au moment de la validation de la commande par le client.
                  </p>

                  <h3 className="h6 fw-semibold mt-4 mb-2">4.3. Promotions</h3>
                  <p>
                    Les offres promotionnelles sont valables dans la limite des stocks disponibles et
                    pendant la durée indiquée. Elles ne sont pas cumulables sauf mention contraire.
                  </p>
                </section>

                <section className="mb-5">
                  <h2 className="h4 fw-bold mb-3">5. Commande</h2>
                  
                  <h3 className="h6 fw-semibold mt-4 mb-2">5.1. Processus de commande</h3>
                  <p>Pour passer commande, le client doit :</p>
                  <ol>
                    <li>Se connecter à son compte Assistance Video</li>
                    <li>Accéder à la page « Acheter des crédits »</li>
                    <li>Sélectionner le pack souhaité</li>
                    <li>Cliquer sur « Acheter »</li>
                    <li>Être redirigé vers la page de paiement sécurisée Stripe</li>
                    <li>Renseigner ses informations de paiement</li>
                    <li>Valider le paiement</li>
                  </ol>

                  <h3 className="h6 fw-semibold mt-4 mb-2">5.2. Validation de la commande</h3>
                  <p>
                    La commande est considérée comme définitive après validation du paiement par Stripe.
                    Le client reçoit immédiatement une confirmation par email et les crédits sont
                    automatiquement ajoutés à son compte.
                  </p>

                  <h3 className="h6 fw-semibold mt-4 mb-2">5.3. Preuve de la transaction</h3>
                  <p>
                    L'enregistrement de la commande dans les systèmes informatiques de LEAGORA et de
                    Stripe constitue la preuve de la transaction. Cette preuve est conservée conformément
                    aux obligations légales.
                  </p>
                </section>

                <section className="mb-5">
                  <h2 className="h4 fw-bold mb-3">6. Paiement</h2>
                  
                  <h3 className="h6 fw-semibold mt-4 mb-2">6.1. Modalités de paiement</h3>
                  <p>
                    Le paiement s'effectue en ligne par carte bancaire via la plateforme sécurisée
                    <strong> Stripe</strong>. Les moyens de paiement acceptés sont :
                  </p>
                  <ul>
                    <li>Cartes bancaires (Visa, Mastercard, American Express)</li>
                    <li>Cartes de débit</li>
                    <li>Autres moyens proposés par Stripe selon votre pays</li>
                  </ul>

                  <h3 className="h6 fw-semibold mt-4 mb-2">6.2. Sécurité du paiement</h3>
                  <p>
                    Les paiements sont entièrement sécurisés par Stripe, certifié PCI-DSS niveau 1
                    (le plus haut niveau de certification de l'industrie des cartes de paiement).
                    LEAGORA ne collecte ni ne stocke aucune donnée bancaire.
                  </p>

                  <h3 className="h6 fw-semibold mt-4 mb-2">6.3. Paiement immédiat</h3>
                  <p>
                    Le paiement est exigible immédiatement lors de la commande. Les crédits ne sont
                    crédités sur le compte qu'après validation du paiement par Stripe.
                  </p>

                  <h3 className="h6 fw-semibold mt-4 mb-2">6.4. Échec de paiement</h3>
                  <p>
                    En cas d'échec du paiement, la commande est annulée. Le client peut réessayer ou
                    contacter le support en cas de problème persistant.
                  </p>
                </section>

                <section className="mb-5">
                  <h2 className="h4 fw-bold mb-3">7. Livraison</h2>
                  
                  <h3 className="h6 fw-semibold mt-4 mb-2">7.1. Livraison instantanée</h3>
                  <p>
                    Les crédits sont livrés instantanément après validation du paiement. Ils sont
                    automatiquement ajoutés au compte du client et immédiatement utilisables.
                  </p>

                  <h3 className="h6 fw-semibold mt-4 mb-2">7.2. Confirmation</h3>
                  <p>
                    Le client reçoit un email de confirmation contenant :
                  </p>
                  <ul>
                    <li>Le récapitulatif de la commande</li>
                    <li>Le nombre de crédits achetés</li>
                    <li>Le montant payé</li>
                    <li>La facture (en pièce jointe ou lien de téléchargement)</li>
                  </ul>
                </section>

                <section className="mb-5">
                  <h2 className="h4 fw-bold mb-3">8. Facturation</h2>
                  
                  <h3 className="h6 fw-semibold mt-4 mb-2">8.1. Émission de la facture</h3>
                  <p>
                    Une facture est automatiquement générée et envoyée par email après chaque achat.
                    La facture est également accessible depuis le compte client.
                  </p>

                  <h3 className="h6 fw-semibold mt-4 mb-2">8.2. Informations de facturation</h3>
                  <p>
                    Le client est responsable de l'exactitude des informations de facturation fournies.
                    Pour modifier ces informations, il doit contacter le support avant la commande.
                  </p>

                  <h3 className="h6 fw-semibold mt-4 mb-2">8.3. Conservation des factures</h3>
                  <p>
                    Les factures sont conservées conformément aux obligations légales (10 ans minimum).
                    Le client peut à tout moment demander un duplicata de ses factures.
                  </p>
                </section>

                <section className="mb-5">
                  <h2 className="h4 fw-bold mb-3">9. Garanties</h2>
                  
                  <h3 className="h6 fw-semibold mt-4 mb-2">9.1. Garantie de conformité</h3>
                  <p>
                    LEAGORA garantit que les crédits achetés seront conformes à la description et
                    permettront de créer des sessions vidéo dans les conditions décrites.
                  </p>

                  <h3 className="h6 fw-semibold mt-4 mb-2">9.2. Garantie de disponibilité</h3>
                  <p>
                    LEAGORA s'efforce d'assurer la disponibilité du service mais ne peut garantir une
                    disponibilité absolue. En cas d'indisponibilité prolongée empêchant l'utilisation
                    des crédits, le client peut demander un avoir ou un remboursement proportionnel.
                  </p>
                </section>

                <section className="mb-5">
                  <h2 className="h4 fw-bold mb-3">10. Service après-vente et réclamations</h2>
                  
                  <h3 className="h6 fw-semibold mt-4 mb-2">10.1. Support client</h3>
                  <p>
                    Pour toute question ou problème concernant une commande, le client peut contacter
                    le support client :
                  </p>
                  <ul className="list-unstyled ms-3">
                    <li><strong>Email :</strong> <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a></li>
                    <li><strong>Délai de réponse :</strong> Sous 48h ouvrées</li>
                  </ul>

                  <h3 className="h6 fw-semibold mt-4 mb-2">10.2. Réclamations</h3>
                  <p>
                    Toute réclamation doit être adressée par email à{' '}
                    <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a> en précisant :
                  </p>
                  <ul>
                    <li>Le numéro de commande</li>
                    <li>La date d'achat</li>
                    <li>La nature du problème</li>
                    <li>Les pièces justificatives éventuelles</li>
                  </ul>
                </section>

                <section className="mb-5">
                  <h2 className="h4 fw-bold mb-3">11. Médiation</h2>
                  <p>
                    Conformément à l'article L612-1 du Code de la consommation, le client a le droit de
                    recourir gratuitement à un médiateur de la consommation en vue de la résolution
                    amiable d'un litige l'opposant à LEAGORA.
                  </p>
                  <p>
                    Le client peut saisir le médiateur de la consommation compétent après avoir tenté,
                    au préalable, de résoudre son litige directement auprès de LEAGORA par une réclamation
                    écrite adressée à <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a>.
                  </p>
                  <p>LEAGORA a désigné le médiateur suivant :</p>
                  <ul className="list-unstyled ms-3">
                    <li><strong>Médiateur :</strong> {MEDIATOR.name}</li>
                    <li><strong>Adresse :</strong> {MEDIATOR.address}</li>
                    <li><strong>Téléphone :</strong> {MEDIATOR.phone}</li>
                    <li><strong>Email :</strong> <a href={`mailto:${MEDIATOR.email}`}>{MEDIATOR.email}</a></li>
                    <li><strong>Site web :</strong> <a href={MEDIATOR.website} target="_blank" rel="noopener noreferrer">{MEDIATOR.websiteLabel}</a></li>
                  </ul>
                  <p className="small text-muted">
                    La saisine du médiateur peut s'effectuer en ligne via le formulaire disponible sur{' '}
                    <a href={MEDIATOR.website} target="_blank" rel="noopener noreferrer">{MEDIATOR.websiteLabel}</a>,
                    par email ou par courrier postal.
                  </p>
                </section>

                <section className="mb-5">
                  <h2 className="h4 fw-bold mb-3">12. Données personnelles</h2>
                  <p>
                    Les données personnelles collectées lors de la commande sont traitées conformément
                    à notre <Link to="/legal/privacy/">Politique de Confidentialité</Link> et au RGPD.
                  </p>
                  <p>
                    Les données de paiement sont collectées et traitées directement par Stripe.
                    LEAGORA ne collecte ni ne stocke aucune donnée bancaire.
                  </p>
                </section>

                <section className="mb-5">
                  <h2 className="h4 fw-bold mb-3">13. Force majeure</h2>
                  <p>
                    LEAGORA ne pourra être tenue responsable de tout retard ou inexécution de ses
                    obligations résultant d'un cas de force majeure tel que défini par la jurisprudence
                    française (catastrophe naturelle, guerre, grève, panne informatique majeure, etc.).
                  </p>
                </section>

                <section className="mb-5">
                  <h2 className="h4 fw-bold mb-3">14. Loi applicable et juridiction</h2>
                  <p>
                    Les présentes CGV sont régies par le droit français. En cas de litige et à défaut
                    d'accord amiable, le litige sera porté devant les tribunaux compétents conformément
                    aux règles de compétence en vigueur.
                  </p>
                  <p>
                    Pour les clients consommateurs domiciliés en France, les litiges peuvent être portés
                    devant le tribunal du lieu où demeure le défendeur ou du lieu de livraison effective
                    du produit.
                  </p>
                </section>

                <section className="mb-5">
                  <h2 className="h4 fw-bold mb-3">15. Modification des CGV</h2>
                  <p>
                    LEAGORA se réserve le droit de modifier les présentes CGV à tout moment. Les CGV
                    applicables sont celles en vigueur au moment de la passation de la commande.
                  </p>
                </section>
              </Card.Body>
            </Card>

            {/* Navigation vers autres pages légales */}
            <div className="mt-4 text-center">
              <p className="text-muted small mb-2">Voir aussi :</p>
              <div className="d-flex flex-wrap justify-content-center gap-3">
                <Link to="/legal/mentions/" className="text-decoration-none">Mentions légales</Link>
                <span className="text-muted">•</span>
                <Link to="/legal/cgu/" className="text-decoration-none">CGU</Link>
                <span className="text-muted">•</span>
                <Link to="/legal/privacy/" className="text-decoration-none">Confidentialité</Link>
                <span className="text-muted">•</span>
                <Link to="/legal/rgpd/" className="text-decoration-none">RGPD</Link>
              </div>
            </div>
          </Col>
        </Row>
      </Container>

      <PublicSiteFooter />
    </div>
  );
};

export default CgvPage;

